<template>
    <el-form-item :label="label" :prop="prop">
        <VueDatePicker
            v-model="selectedTime"
            :clearable="clearable"
            time-picker
            :is-24="is24"
            teleport-center
            auto-apply
            :format="is24 ? 'HH:mm' : 'hh:mm aa'"
        />
    </el-form-item>
</template>

<script lang="ts" setup>
    import { ref, watch, onMounted, computed } from 'vue'
    import { parse, format } from 'date-fns'
    import type { PropType } from 'vue'

    interface TimeObject {
        hours: number
        minutes: number
    }

    const props = defineProps({
        modelValue: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: 'Time',
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        is24: {
            type: Boolean,
            default: false,
        },
        prop: {
            type: [String, Array] as PropType<string | string[]>,
            default: ''
        },
    })

    const emit = defineEmits(['update:modelValue'])

    const selectedTime = ref<TimeObject>({
        hours: 12,
        minutes: 0,
    })

    // Parse time string to hours and minutes
    const parseTimeString = (timeString: string): TimeObject => {
        if (!timeString) {
            const now = new Date()
            return {
                hours: now.getHours(),
                minutes: now.getMinutes()
            }
        }

        try {
            // Handle different time formats (HH:mm, HH:mm:ss, etc.)
            const timeParts = timeString.split(':')
            const hours = parseInt(timeParts[0], 10)
            const minutes = parseInt(timeParts[1], 10)
            
            return {
                hours: isNaN(hours) ? 12 : hours,
                minutes: isNaN(minutes) ? 0 : minutes
            }
        } catch {
            return { hours: 12, minutes: 0 }
        }
    }

    // Format time object to string
    const formatTimeToString = (time: TimeObject): string => {
        return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}`
    }

    onMounted(() => {
        if (props.modelValue) {
            selectedTime.value = parseTimeString(props.modelValue)
        }
    })

    // Watch for changes from parent
    watch(() => props.modelValue, (newValue) => {
        if (newValue) {
            const parsedTime = parseTimeString(newValue)
            // Only update if different to avoid infinite loop
            if (parsedTime.hours !== selectedTime.value.hours || 
                parsedTime.minutes !== selectedTime.value.minutes) {
                selectedTime.value = parsedTime
            }
        }
    }, { immediate: true })

    // Watch for changes from component and emit to parent
    watch(selectedTime, (newValue) => {
        const formattedTime = formatTimeToString(newValue)
        emit('update:modelValue', formattedTime)
    }, { deep: true })
</script>